import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getTier1Services } from "@/data/services";
import OptimizedImage from "@/components/OptimizedImage";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const ServicesPreview = () => {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.1, triggerOnce: true });
  // Use Tier 1 services for homepage preview
  const featuredServices = getTier1Services();

  return (
    <section ref={ref} className="py-16 md:py-24 bg-muted/20 relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-accent/3 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div 
          className={`text-center mb-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-medium bg-accent/10 text-accent rounded-full ring-1 ring-accent/20">
            <Star className="h-4 w-4 fill-current" />
            Most Popular
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Marketing, Apps, & Complete Business Platforms
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From marketing and SEO to custom CRMs and integrated platforms—we build exactly what your business needs.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {featuredServices.map((service, index) => (
            <Link
              key={service.id}
              to={`/services/${service.id}`}
              className={`block transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${200 + index * 100}ms` }}
            >
              <Card 
                variant="premium" 
                className="group h-full overflow-hidden hover:shadow-2xl hover:shadow-accent/10"
              >
                <div className="relative overflow-hidden">
                  <OptimizedImage 
                    src={service.image} 
                    alt={service.title}
                    aspectRatio="video"
                    priority={index < 3}
                    className="group-hover:scale-105 transition-transform duration-500 ease-out"
                  />
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xl group-hover:text-accent transition-colors duration-300">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4 text-sm">
                    {service.tagline}
                  </p>
                  {service.resultMetric && (
                    <p className="inline-block text-sm font-medium text-accent bg-accent/10 px-3 py-1 rounded-full">
                      {service.resultMetric}
                    </p>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}

          {/* View All Services Card */}
          <Card 
            variant="float"
            className={`flex items-center justify-center border-dashed border-2 border-border hover:border-accent/50 transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '800ms' }}
          >
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground mb-4">Need something specific?</p>
              <Button variant="outline" asChild className="group hover:border-accent/50">
                <Link to="/services">
                  View All Services
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;
